import { Subscription } from 'rxjs';
import { AuthService, User } from '../../services/auth.service';
import { ConfigService } from '../../services/config.service';
import { ModalController } from '@ionic/angular';
import { TeamsService } from '../../services/teams.service';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastController } from '@ionic/angular';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Validators } from '@angular/forms';
import { AnalyticsService } from '../../services/analytics.service';

@Component({
  selector: 'rename-modal',
  templateUrl: 'rename-modal.html',
  styleUrls: ['./rename-modal.scss'],
})
export class RenameModal implements OnInit, OnDestroy {
  // Data passed in by componentProps
  @Input() action: string;
  @Input() data: any;

  renameForm: FormGroup;
  userData: User;
  userDataSubscription: Subscription;

  constructor(
    private analytics: AnalyticsService,
    public modalCtrl: ModalController,
    public toastController: ToastController,
    public auth: AuthService,
    public teamsService: TeamsService,
    public config: ConfigService,
    public translate: TranslateService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.userDataSubscription = this.auth.userData$.subscribe(
      async (userData: User) => {
        if (userData) {
          this.userData = userData;
        }
      },
      (error: any) => {
        // JL_TODO: send this as a subscription crash
        console.log('user subscription error', error);
        // debugger;
      }
    );
    this.setFormValues();
  }

  ngOnDestroy() {
    if (this.userDataSubscription) {
      this.userDataSubscription.unsubscribe();
    }
  }

  setFormValues() {
    let formData;
    switch (this.action) {
      case 'TEAMS.ACTIONS.RENAME_TEAM':
        formData = {
          teamName: [this.data.team.teamName, Validators.required],
        };
        break;
      case 'TEAMS.ACTIONS.EDIT_INVITATION':

        let invitation = `${this.userData.firstName} ${this.userData.lastName} has invited you to ${this.data.team.teamName}`;

        if(this.data.team.teamInvitation !== undefined ) {
          invitation = this.data.team.teamInvitation;
        }

        formData = {
          teamInvitation: [ invitation, Validators.required],
        };
        break;
      case 'TEAMS.ACTIONS.RENAME_MANAGER':
        formData = {
          teamManagerFirstName: [
            this.data.team.teamManagerFirstName,
            Validators.required,
          ],
          teamManagerLastName: [
            this.data.team.teamManagerLastName,
            Validators.required,
          ],
        };
        break;
      case 'STAFF.ACTIONS.EDIT_STAFF':
      case 'MEMBERS.ACTIONS.EDIT_MEMBER':
        formData = {};
        break;
      default:
        console.log(`RenameModal: Invalid action ${this.action}`);
    }
    this.renameForm = this.fb.group(formData);
  }

  dismiss(ui = true) {
    if (!ui) {
      switch (this.action) {
        case 'TEAMS.ACTIONS.RENAME_TEAM':
          this.analytics.track('qt_team_rename_cancel', {
            ui: true,
            team_id: this.data.team.teamId,
          });
          break;

        case 'TEAMS.ACTIONS.EDIT_INVITATION':
          this.analytics.track('qt_invitation_edit_cancel', {
            ui: true,
            team_id: this.data.team.teamId,
          });
          break;

        case 'TEAMS.ACTIONS.RENAME_MANAGER':
          this.analytics.track('qt_team_manager_cancel', {
            ui: true,
            team_id: this.data.team.teamId,
          });
          break;
        case 'MEMBERS.ACTIONS.EDIT_MEMBER':
        case 'STAFF.ACTIONS.EDIT_STAFF':
          this.analytics.track('qt_member_edit_cancel', {
            ui: true,
            team_id: this.data.team.teamId,
            member_type: this.data.member.memberType,
          });
          break;
      }
    }
    this.modalCtrl.dismiss();
  }

  save() {
    console.log('save changes');
    switch (this.action) {
      case 'TEAMS.ACTIONS.RENAME_TEAM':
        this.analytics.track('qt_team_rename_save', {
          ui: true,
          team_id: this.data.team.teamId,
        });

        // Check Valid Team Name
        if (this.renameForm.get('teamName').invalid) {
          this.analytics.track('qt_input_required', {
            source: 'new-team-page',
            input: 'teamName',
          });
          this.required('TEAMS.ERRORS.TEAM_NAME_REQUIRED');
          return;
        }
        if (
          !this.teamsService.isTeamNameAvailable(
            this.renameForm.get('teamName').value
          )
        ) {
          this.analytics.track('qt_team_duplicate', {
            source: 'new-team-page',
          });
          this.required('TEAMS.ERRORS.DUPLICATE_TEAM_NAME');
          return;
        }

        if (
          this.teamsService.hasPhoneOrURL(this.renameForm.get('teamName').value)
        ) {
          this.analytics.track('qt_input_invalid', {
            source: 'new-team-page',
            input: 'teamName',
          });

          this.required('TEAMS.ERRORS.TEAM_NAME_INVALID');
          return;
        }

        this.teamsService.teamUpdate(this.data.team.teamId, {
          teamName: this.renameForm.get('teamName').value,
        });
        this.dismiss(false);
        break;
      case 'TEAMS.ACTIONS.EDIT_INVITATION':
        if (this.renameForm.get('teamInvitation').invalid) {
          this.analytics.track('qt_invitation_invalid', {
            source: 'new-team-page',
            input: 'teamName',
          });
          this.required('TEAMS.ERRORS.INVITATION_REQUIRED');
          return;
        }

        if (
          this.teamsService.hasPhoneOrURL(
            this.renameForm.get('teamInvitation').value
          )
        ) {
          this.analytics.track('qt_invitation_invalid', {
            source: 'new-team-page',
            input: 'teamInvitation',
          });

          this.required('TEAMS.ERRORS.INVITATION_INVALID');
          return;
        }

        this.teamsService.teamUpdate(this.data.team.teamId, {
          teamInvitation: this.renameForm.get('teamInvitation').value,
        });

        this.dismiss(false);
        break;
      case 'TEAMS.ACTIONS.RENAME_MANAGER':
        this.analytics.track('qt_team_manager_save', {
          ui: true,
          team_id: this.data.team.teamId,
        });
        this.teamsService.teamUpdate(this.data.team.teamId, {
          teamManagerFirstName: this.renameForm.get('teamManagerFirstName')
            .value,
          teamManagerLastName: this.renameForm.get('teamManagerLastName').value,
        });
        this.dismiss(false);
        break;
      case 'MEMBERS.ACTIONS.EDIT_MEMBER':
      case 'STAFF.ACTIONS.EDIT_STAFF':
        this.analytics.track('qt_member_edit_save', {
          ui: true,
          team_id: this.data.member.memberId,
          member_type: this.data.member.memberType,
        });
        this.data.op = 'save';
        break;
    }
  }

  async required(msg) {
    const toast = await this.toastController.create({
      message: this.translate.instant(msg),
      duration: 2000,
      color: 'danger',
    });
    toast.present();
  }
}
